import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import { connect } from 'react-redux';
import useEmbedPdf from 'hooks/useEmbedPdf';
import useScrolledToBottomOfPdf from 'hooks/useScrolledToBottomOfPdf';
import { HOME_ROOT, PAGES_ROOT } from 'config/api';
import dialogActions from 'core/dialogs/actions';
import pageActions from 'core/page/actions';
import Button from 'common/Button';
import Spinner from 'common/Spinner';
import { PencilNextIcon } from '@glu/icons-react';
import isMobileDevice from 'utils/isMobileDevice';

import inputStyles from 'fields/styles/input';
import RawHtml from 'common/RawHtml';
import dialogStyles from './styles/dialog';
import contentStyles from './styles/content';
import bodyStyles from './styles/body';
import footerStyles from './styles/footer';
import noticeStyles from './styles/notice';
import embedPdf from './styles/embedPdf';

const styles = (theme) => {
  const common = {
    body: {
      ...bodyStyles(theme),
      '& > div': {
        height: '100%',
        [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
          height: 'calc(100% - 50px)',
        },
      },
    },
    content: {
      ...contentStyles(theme),
      overflow: 'hidden',
    },
    dialog: dialogStyles(theme),
    embedPdf: embedPdf(theme),
    footer: {
      ...footerStyles(theme),
      [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
        '&.hideSignature': {
          transform: 'translateY(100%)',
        },
        background: theme.palette.background,
        bottom: 0,
        left: 0,
        padding: '0.5em',
        position: 'absolute',
        right: 0,
        transition: 'transform .3s',
      },
    },
    notice: {
      ...noticeStyles(theme),
      paddingBottom: 12,
    },
  };
  return {
    ...common,
    canvas: {
      border: `1px solid ${theme.palette.form.border}`,
      display: 'block',
      margin: [theme.spacing.unit, 0, theme.spacing.xs],
      maxHeight: 77,
      touchAction: 'none',
      width: '100%',
    },
    canvasWrap: {
      '& p': {
        color: '#bebebe',
        fontSize: 18,
        position: 'absolute',
        textAlign: 'center',
        top: 'calc(50% - 9px)',
        width: '100%',
        zIndex: -1,
      },
      position: 'relative',
    },
    content: {
      ...common.content,
      borderLeft: 'none',
      display: 'flex',
    },
    dialog: {
      ...common.dialog,
      height: '90%',
      maxWidth: '100%',
      width: '90%',

      [`@media(max-width: ${theme.screenSizes.mobileLandscapeMax}px)`]: {
        height: '100%',
        width: '100%',
      },
    },
    esignInput: {
      ...inputStyles(theme),
      '&::placeholder': {
        color: theme.palette.grey.light,
        fontFamily: theme.typography.fontFamilies.secondary,
        fontSize: 18,
        textAlign: 'center',
      },
      border: `1px solid ${theme.palette.form.border}`,
      fontFamily: 'Allura',
      fontSize: 36,
      marginBottom: theme.spacing.md,
      padding: [5, 10],
    },
    esignername: {
      marginBottom: 10,
    },
    footerToggle: {
      '& .pencil': {
        '& svg': {
          fill: theme.palette.background,
          height: 14,
          width: 14,
        },
        alignItems: 'center',
        background: theme.palette.icon,
        borderRadius: '50%',
        display: 'flex',
        height: 24,
        justifyContent: 'center',
        width: 24,
      },
      [`@media(min-width: ${theme.screenSizes.mobileLandscapeMax}px)`]: {
        display: 'none',
      },
      '& span': {
        fontSize: 14,
        lineHeight: '18px',
        marginRight: theme.spacing.unit * 2,
      },
      '&.body': {
        borderRadius: 0,
        bottom: 0,
        height: 50,
        left: 0,
        margin: [0, 'auto'],
        position: 'absolute',
        right: 0,
        top: 'inherit',
        width: '100%',
      },
      alignItems: 'center',
      background: 'none',
      border: 0,
      display: 'flex',
      justifyContent: 'center',
      margin: [0, 'auto', '0.5em'],
      padding: 0,
    },
    wrapper: {
      '&.error': {
        color: theme.palette.error,
      },
      margin: [0, 'auto'],
      maxWidth: 420,
    },
  };
};

export const EsignBare = ({
  agreement,
  agreementCanvasPlaceholderLabel,
  agreementCustomerNameAndRole,
  agreementHideSignaturePadLabel,
  agreementInputPlaceholderLabel,
  agreementShowSignaturePadLabel,
  classes,
  close,
  commonClasses,
  errorMsg,
  id,
  isHomeEsign,
  loading,
  pageName,
  request,
  saveSignature,
  saveSignatureHome,
  signModalAcceptLabel,
  signModalDeclineLabel,
  signModalLabel,
  viewTheEntireDocumentLabel,
}) => {
  const [disabledButton, setDisabledButton] = useState(true);
  const [error, setError] = useState(null);
  const [hideSignature, setHideSignature] = useState(true);
  const [signature, setSignature] = useState(null);
  const [hasStartedSignature, setHasStartedSignature] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobileDevice());
  const canvas = useRef(null);
  const mousePressed = useRef(false);
  const lastX = useRef(0);
  const lastY = useRef(0);
  const canvasContext = useRef(null);

  useEffect(() => {
    canvasContext.current = canvas.current && canvas.current.getContext('2d');
  }, []);
  const agreementEndpoint = isHomeEsign
    ? `${HOME_ROOT}/${request.id}/agreements/${agreement.agreementId}`
    : `${PAGES_ROOT}/${pageName}/agreements/${id}`;
  useEmbedPdf(agreementEndpoint, '#embed-pdf', {
    pdfOpenParams: {
      view: 'FitH',
    },
    title: agreement.label,
  });
  const isScrolledBottom = useScrolledToBottomOfPdf('#embed-pdf');

  const canvasWidth = useMemo(() => (window.innerWidth > 420 ? 420 : window.innerWidth - 40), []);

  const isValidSignature = useCallback(() => {
    let pixelCount = 0;
    const ctx = canvasContext.current;
    const imageData = ctx?.getImageData(0, 0, canvasWidth, 77);
    for (let i = 0; i < imageData?.data?.length; i += 1) {
      if (imageData.data[i]) {
        pixelCount += 1;
      }
    }
    return (pixelCount / imageData?.data?.length) * 100 >= 0.5;
  }, [canvasWidth]);

  const getPosition = (event) => {
    let clientX;
    let clientY;

    if (event.nativeEvent.touches) {
      [{ clientX, clientY }] = event.nativeEvent.touches;
    } else {
      ({ x: clientX, y: clientY } = event.nativeEvent);
    }
    const { left, top } = event.nativeEvent.target.getBoundingClientRect();
    const canvasX = clientX - left;
    const canvasY = clientY - top;

    return { x: canvasX, y: canvasY };
  };

  const touchStart = useCallback((e) => {
    const { current: ctx } = canvasContext;
    const { x, y } = getPosition(e);

    ctx.beginPath();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 3;
    ctx.lineJoin = 'round';
    lastX.current = x;
    lastY.current = y;
    mousePressed.current = true;
  }, []);

  const touchMove = useCallback((e) => {
    const { current: ctx } = canvasContext;

    if (mousePressed.current) {
      setHasStartedSignature(true);
      const { x, y } = getPosition(e);
      ctx.moveTo(lastX.current, lastY.current);
      ctx.lineTo(x, y);
      ctx.closePath();
      ctx.stroke();
      lastX.current = x;
      lastY.current = y;
    }
  }, []);
  const touchEnd = useCallback(() => {
    mousePressed.current = false;
    setDisabledButton(!isScrolledBottom || !isValidSignature());
  }, [isScrolledBottom, isValidSignature]);

  useEffect(() => {
    setDisabledButton(!isScrolledBottom || (!signature && !isValidSignature()));
  }, [isScrolledBottom, isValidSignature, signature]);

  useEffect(() => {
    const updateIsMobile = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  const buttons = [
    {
      className: 'btn btn-mobile btn-i-accept',
      disabled: disabledButton,
      onClick: (e) => {
        e.preventDefault();

        const hasValidMobileSignature = isValidSignature();

        if (!signature && !hasValidMobileSignature) {
          setError(true);
        } else {
          const data = signature || canvas.current.toDataURL('image/png');
          if (isHomeEsign) {
            saveSignatureHome(request.id, agreement.agreementId, data);
          } else {
            saveSignature(id, { signature: data });
          }
        }
      },
      text: signModalAcceptLabel,
      variant: 'primary',
    },
    {
      className: 'btn btn-mobile btn-mobile--secondary btn-i-decline',
      onClick: (e) => {
        e.preventDefault();

        close();
      },
      text: signModalDeclineLabel,
      variant: 'secondary',
    },
  ];
  return (
    <div className={classnames('modal-esign', commonClasses, classes.dialog)}>
      <div className={classnames('modal-content', classes.content)}>
        <div className={classnames('modal-body', classes.body)} id="modal-body">
          <div>
            <div id="embed-pdf" className={classnames('embed-pdf', classes.embedPdf)} />
          </div>
          <Button
            className={classnames('footer-toggle', 'body', classes.footerToggle)}
            variant="secondary"
            onClick={() => setHideSignature((h) => !h)}
          >
            <span>{agreementShowSignaturePadLabel}</span>
            <PencilNextIcon className="pencil" />
          </Button>
        </div>
        <div className={classnames('modal-footer', classes.footer, { hideSignature })}>
          <p className={classnames('modal-notice', classes.notice)}>{viewTheEntireDocumentLabel}</p>
          <div className={classnames('esign-wrapper form-group', classes.wrapper, { error })}>
            {signModalLabel && (
              <label className="esign-label" htmlFor="esignInput">
                {signModalLabel}
              </label>
            )}
            <div className={classnames(classes.esignername)}>
              <RawHtml>{agreementCustomerNameAndRole}</RawHtml>
            </div>
            {isMobile && (
              <div className={classnames('canvas-wrap', classes.canvasWrap)}>
                <canvas
                  ref={canvas}
                  className={classnames('esign-canvas', classes.canvas)}
                  width={canvasWidth}
                  height="77"
                  onTouchEnd={touchEnd}
                  onTouchCancel={touchEnd}
                  onTouchStart={touchStart}
                  onTouchMove={touchMove}
                  onMouseDown={touchStart}
                  onMouseUp={touchEnd}
                  onMouseMove={touchMove}
                >
                  <p>Your browser is not supported.</p>
                </canvas>
                {!hasStartedSignature && <p>{agreementCanvasPlaceholderLabel}</p>}
              </div>
            )}
            <Button
              className={classnames('footer-toggle', classes.footerToggle)}
              variant="secondary"
              onClick={() => setHideSignature((h) => !h)}
            >
              <span>{agreementHideSignaturePadLabel}</span>
              <PencilNextIcon className="pencil" />
            </Button>
            {!isMobile && (
              <input
                className={classnames('esign-input', classes.esignInput)}
                type="text"
                id="esignInput"
                onChange={(e) => setSignature(e.target.value)}
                value={signature || ''}
                autoComplete="off"
                placeholder={agreementInputPlaceholderLabel}
              />
            )}
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className="esign-errorMsg">{errorMsg}</label>
          </div>

          {buttons.map((b, i) => (
            <Button
              /* eslint-disable-next-line react/no-array-index-key */
              key={`dialog-button-${i}`}
              className={classnames(`btn ${b.className} popbox-btn${i}`, { loading })}
              data-key={i}
              onClick={b.onClick}
              disabled={!!b.disabled || loading}
              variant={b.variant}
            >
              {b.text}
              {b.className === 'btn btn-mobile btn-i-accept' && (
                <>
                  <i className="icon-arrow-thin-right" aria-hidden="true" />
                  <Spinner loading={loading} />
                </>
              )}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

EsignBare.propTypes = {
  agreement: PropTypes.shape({
    agreementId: PropTypes.number,
    label: PropTypes.string,
  }),
  agreementCanvasPlaceholderLabel: PropTypes.string,
  agreementCustomerNameAndRole: PropTypes.string,
  agreementHideSignaturePadLabel: PropTypes.string,
  agreementInputPlaceholderLabel: PropTypes.string,
  agreementShowSignaturePadLabel: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  close: PropTypes.func.isRequired,
  commonClasses: PropTypes.string.isRequired,
  errorMsg: PropTypes.string,
  id: PropTypes.number,
  isHomeEsign: PropTypes.bool,
  loading: PropTypes.bool,
  pageName: PropTypes.string.isRequired,
  request: PropTypes.shape({
    id: PropTypes.number,
  }),
  saveSignature: PropTypes.func.isRequired,
  saveSignatureHome: PropTypes.func.isRequired,
  signModalAcceptLabel: PropTypes.string,
  signModalDeclineLabel: PropTypes.string,
  signModalLabel: PropTypes.string,
  viewTheEntireDocumentLabel: PropTypes.string,
};

EsignBare.defaultProps = {
  agreement: {},
  agreementCanvasPlaceholderLabel: 'Please sign here.',
  agreementCustomerNameAndRole: '',
  agreementHideSignaturePadLabel: 'Hide Signature Pad',
  agreementInputPlaceholderLabel: 'Please type signature here.',
  agreementShowSignaturePadLabel: 'Show Signature Pad',
  errorMsg: '',
  id: undefined,
  isHomeEsign: false,
  loading: false,
  request: {},
  signModalAcceptLabel: 'Sign',
  signModalDeclineLabel: 'Close',
  signModalLabel: '',
  viewTheEntireDocumentLabel: 'View the entire document to accept the terms.',
};

const mapStateToProps = ({ dialogs, page, resources }) => ({
  agreement: dialogs.params.agreement,
  agreementCanvasPlaceholderLabel: resources.agreementCanvasPlaceholderLabel,
  agreementCustomerNameAndRole: resources.agreementCustomerNameAndRole,
  agreementHideSignaturePadLabel: resources.agreementHideSignaturePadLabel,
  agreementInputPlaceholderLabel: resources.agreementInputPlaceholderLabel,
  agreementShowSignaturePadLabel: resources.agreementShowSignaturePadLabel,
  errorMsg: dialogs.params.errorMsg,
  id: dialogs.params.id,
  loading: dialogs.params.loading,
  pageName: page.name,
  request: dialogs.params.request,
  signModalAcceptLabel: dialogs.params.signModalAcceptLabel,
  signModalDeclineLabel: dialogs.params.signModalDeclineLabel,
  signModalLabel: dialogs.params.signModalLabel,
  viewTheEntireDocumentLabel: resources.viewTheEntireDocumentLabel,
});

const EsignStyled = withStyles(styles)(EsignBare);

export default connect(mapStateToProps, {
  close: dialogActions.close,
  saveSignature: pageActions.saveSignature,
  saveSignatureHome: dialogActions.sign,
})(EsignStyled);
